import { css } from 'lit';

export const styles = css`
  /* ─── Host ──────────────────────────────────────────────────── */
  :host {
    display: block;
    box-sizing: border-box;
    font-family: var(--asc-font-family-body, 'aktiv-grotesk', sans-serif);
  }

  /* ─── Outer container ───────────────────────────────────────── */
  [part='base'] {
    display: flex;
    flex-direction: column;
    background: var(--asc-table-bg, var(--asc-bg-primary));
    overflow: hidden;
  }

  /* Card variant — full border + radius + shadow */
  :host([variant='card']) [part='base'],
  :host(:not([variant])) [part='base'] {
    border: 1px solid var(--asc-table-border-color, var(--asc-border-secondary));
    border-radius: var(--asc-table-border-radius, var(--asc-radius-lg));
    box-shadow: var(
      --asc-table-shadow,
      0px 1px 2px 0px var(--asc-shadow-xs)
    );
  }

  /* Flat variant — top + bottom border only */
  :host([variant='flat']) [part='base'] {
    border-top: 1px solid var(--asc-table-border-color, var(--asc-border-secondary));
    border-bottom: 1px solid var(--asc-table-border-color, var(--asc-border-secondary));
    border-radius: 0;
    box-shadow: none;
  }

  /* ─── Header area ───────────────────────────────────────────── */
  [part='header'] {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: var(--asc-table-header-actions-gap, var(--asc-spacing-xl));
    padding: var(--asc-table-header-padding-top, var(--asc-spacing-2xl))
      var(--asc-table-header-padding-x, var(--asc-spacing-3xl));
    background: var(--asc-table-header-bg, var(--asc-bg-primary));
    border-bottom: 1px solid
      var(--asc-table-header-border-color, var(--asc-border-secondary));
  }

  [part='header-content'] {
    display: flex;
    flex-direction: column;
    gap: var(--asc-table-header-text-gap, var(--asc-spacing-xxs));
    min-width: 0;
  }

  [part='header-content'] ::slotted(*) {
    margin: 0;
    padding: 0;
  }

  [part='header-content'] ::slotted([slot='title']) {
    color: var(--asc-table-title-color, var(--asc-text-primary));
    font-size: var(--asc-table-title-font-size, var(--asc-font-size-text-lg));
    font-weight: var(
      --asc-table-title-font-weight,
      var(--asc-font-weight-semibold)
    );
    line-height: var(--asc-table-title-line-height, var(--asc-line-height-text-lg));
  }

  [part='header-content'] ::slotted([slot='description']) {
    color: var(--asc-table-description-color, var(--asc-text-tertiary));
    font-size: var(
      --asc-table-description-font-size,
      var(--asc-font-size-text-sm)
    );
    font-weight: var(
      --asc-table-description-font-weight,
      var(--asc-font-weight-regular)
    );
    line-height: var(--asc-line-height-text-sm);
  }

  [part='header-actions'] {
    display: flex;
    align-items: center;
    gap: var(--asc-spacing-sm);
    flex-shrink: 0;
  }

  /* Mobile: tighter header padding */
  @media (max-width: 640px) {
    [part='header'] {
      padding: var(--asc-table-header-padding-top, var(--asc-spacing-2xl))
        var(--asc-spacing-xl);
    }
  }

  /* ─── Table scroll container ────────────────────────────────── */
  [part='table-container'] {
    overflow-x: auto;
    width: 100%;
  }

  /* ─── Native table element ──────────────────────────────────── */
  [part='table'] {
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    table-layout: auto;
  }

  /* ─── Caption (visually hidden, accessible name) ────────────── */
  [part='caption'] {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* ─── Column header row ─────────────────────────────────────── */
  [part='thead'] {
    position: sticky;
    top: 0;
    z-index: 1;
  }

  [part='col-header'],
  [part='checkbox-col-header'] {
    height: var(--asc-table-col-header-height, 44px);
    padding: var(--asc-table-col-header-padding-y, var(--asc-spacing-lg))
      var(--asc-table-col-header-padding-x, var(--asc-spacing-3xl));
    background: var(--asc-table-col-header-bg, var(--asc-bg-secondary));
    color: var(--asc-table-col-header-color, var(--asc-text-quaternary));
    font-size: var(
      --asc-table-col-header-font-size,
      var(--asc-font-size-text-xs)
    );
    font-weight: var(
      --asc-table-col-header-font-weight,
      var(--asc-font-weight-semibold)
    );
    line-height: var(
      --asc-table-col-header-line-height,
      var(--asc-line-height-text-xs)
    );
    text-align: left;
    white-space: nowrap;
    border-bottom: 1px solid
      var(--asc-table-col-header-border-color, var(--asc-border-secondary));
    box-sizing: border-box;
    vertical-align: middle;
  }

  /* Hover: darken text */
  [part='col-header']:hover {
    color: var(--asc-table-col-header-color-hover, var(--asc-text-secondary));
  }

  /* Checkbox column header */
  [part='checkbox-col-header'] {
    width: var(--asc-table-checkbox-col-width, 44px);
    padding-left: var(--asc-spacing-lg);
    padding-right: var(--asc-spacing-lg);
  }

  /* Focus ring on header cells */
  [part='col-header']:focus-visible,
  [part='checkbox-col-header']:focus-visible {
    outline: 2px solid var(--asc-focus-ring);
    outline-offset: -2px;
  }

  /* Disabled header opacity */
  :host([disabled]) [part='col-header'],
  :host([disabled]) [part='checkbox-col-header'] {
    opacity: 0.6;
  }

  /* ─── Sort button ───────────────────────────────────────────── */
  [part='sort-button'] {
    display: inline-flex;
    align-items: center;
    gap: var(--asc-table-sort-icon-gap, var(--asc-spacing-xs));
    background: none;
    border: none;
    padding: 2px var(--asc-spacing-xs);
    /* Negative margin cancels the button's own padding so the label aligns
       with non-sortable column header text at the same <th> padding edge */
    margin: -2px calc(-1 * var(--asc-spacing-xs));
    cursor: pointer;
    color: inherit;
    font: inherit;
    white-space: nowrap;
    border-radius: var(--asc-radius-xs);
    transition: background-color var(--asc-transition-fast);
  }

  [part='sort-button']:hover:not([disabled]) {
    color: var(--asc-table-col-header-color-hover, var(--asc-text-secondary));
  }

  [part='sort-button']:focus-visible {
    outline: 2px solid var(--asc-focus-ring);
    outline-offset: 2px;
  }

  [part='sort-button'][disabled] {
    cursor: not-allowed;
    opacity: 0.5;
  }

  [part='sort-icon'] {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 12px;
    height: 12px;
    color: var(--asc-table-sort-icon-color, var(--asc-text-quaternary));
    flex-shrink: 0;
  }

  /* Sort icon inherits column header text color */
  [part='col-header']:hover [part='sort-icon'] {
    color: var(--asc-table-col-header-color-hover, var(--asc-text-secondary));
  }

  /* ─── Styled checkboxes ─────────────────────────────────────── */
  .checkbox {
    appearance: none;
    -webkit-appearance: none;
    display: inline-block;
    width: var(--asc-table-checkbox-size, 20px);
    height: var(--asc-table-checkbox-size, 20px);
    border: 1.5px solid
      var(--asc-table-checkbox-border-color, var(--asc-border-primary));
    border-radius: var(
      --asc-table-checkbox-border-radius,
      var(--asc-spacing-sm)
    );
    background: var(--asc-bg-primary);
    cursor: pointer;
    flex-shrink: 0;
    position: relative;
    vertical-align: middle;
    transition:
      border-color var(--asc-transition-fast),
      background var(--asc-transition-fast);
    box-sizing: border-box;
  }

  .checkbox:checked,
  .checkbox:indeterminate {
    background: var(--asc-bg-brand-solid);
    border-color: var(--asc-bg-brand-solid);
  }

  /* Checkmark via pseudo-element */
  .checkbox:checked::after {
    content: '';
    position: absolute;
    left: 5px;
    top: 2px;
    width: 5px;
    height: 9px;
    border: 2px solid var(--asc-color-base-white);
    border-top: none;
    border-left: none;
    transform: rotate(45deg);
  }

  /* Indeterminate dash */
  .checkbox:indeterminate::after {
    content: '';
    position: absolute;
    left: 3px;
    top: 8px;
    width: 10px;
    height: 2px;
    background: var(--asc-color-base-white);
  }

  .checkbox:focus-visible {
    outline: 2px solid var(--asc-focus-ring);
    outline-offset: 2px;
  }

  .checkbox:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  /* ─── Data rows ─────────────────────────────────────────────── */
  [part='row'] {
    cursor: pointer;
  }

  :host([disabled]) [part='row'] {
    cursor: default;
    pointer-events: none;
  }

  [part='cell'],
  [part='checkbox-cell'] {
    padding: 0;
    vertical-align: top;
    box-sizing: border-box;
    /* height:1px trick lets child asc-table-cell with height:100% fill the full row */
    height: 1px;
  }

  /* Focus ring on data cells */
  [part='cell']:focus-visible,
  [part='checkbox-cell']:focus-visible {
    outline: 2px solid var(--asc-focus-ring);
    outline-offset: -2px;
  }

  /* Checkbox cell — fixed width */
  [part='checkbox-cell'] {
    width: var(--asc-table-checkbox-col-width, 44px);
    vertical-align: middle;
  }

  /* Match checkbox header padding exactly */
  [part='checkbox-cell'] asc-table-cell {
    --asc-table-cell-padding-x-sm: var(--asc-spacing-lg);
    --asc-table-cell-padding-x-md: var(--asc-spacing-lg);
    --asc-table-cell-padding-y-sm: 0px;
    --asc-table-cell-padding-y-md: 0px;
  }

  /* ─── Flat variant: lighter row separators ──────────────────── */
  :host([variant='flat']) [part='cell'] asc-table-cell {
    --asc-table-cell-border-bottom-color: var(
      --asc-table-row-border-color-light,
      var(--asc-border-tertiary)
    );
  }

  :host([variant='flat']) [part='checkbox-cell'] {
    --asc-table-cell-border-bottom-color: var(
      --asc-table-row-border-color-light,
      var(--asc-border-tertiary)
    );
  }

  /* Brand header style — white bg + brand accent bottom border */
  :host([header-style='brand']) [part='col-header'],
  :host([header-style='brand']) [part='checkbox-col-header'] {
    background: var(--asc-table-col-header-bg, var(--asc-bg-primary));
    border-bottom-color: var(
      --asc-table-col-header-border-color,
      var(--asc-border-brand)
    );
  }

  /* ─── Hide header row ───────────────────────────────────────── */
  :host([hide-header]) [part='thead'] {
    display: none;
  }

  /* ─── Empty state ───────────────────────────────────────────── */
  [part='empty-state'] td {
    padding: var(--asc-spacing-4xl);
    text-align: center;
    color: var(--asc-text-tertiary);
    font-size: var(--asc-font-size-text-sm);
  }

  /* ─── Remove bottom border on last row to avoid doubling with footer / container border ── */
  [part='tbody'] tr:last-child asc-table-cell {
    --asc-table-cell-border-bottom-width: 0px;
  }

  /* ─── Skeleton shimmer ──────────────────────────────────────── */
  @keyframes asc-shimmer {
    0% {
      background-position: -400px 0;
    }
    100% {
      background-position: 400px 0;
    }
  }

  .skeleton-cell {
    display: block;
    width: 80%;
    height: 16px;
    border-radius: var(--asc-radius-xs);
    background: linear-gradient(
      90deg,
      var(--asc-table-skeleton-bg, var(--asc-bg-secondary)) 25%,
      var(--asc-table-skeleton-shimmer-color, var(--asc-bg-tertiary)) 50%,
      var(--asc-table-skeleton-bg, var(--asc-bg-secondary)) 75%
    );
    background-size: 800px 100%;
    animation: asc-shimmer 1.5s infinite linear;
  }
`;
